import { SetupInstance } from '../../app/msal-account-helpers';
import { protectedResources } from '../../authConfig';
import { useLocal } from './../environment';

import { InteractionRequiredAuthError, InteractionStatus, BrowserAuthError } from '@azure/msal-browser';
import { hydrateObjects, cleanUpJsonForTransport } from '../json-helper';
import polly from 'polly-js';
import { AccessTokenHelper } from '../access-token-helper';

import { msalConfig } from '../../app';

export class DashboardApi {
    constructor(instance, inProgress, accounts, forceRefresh) {

        this.instance = SetupInstance(instance);
        this.inProgress = inProgress;
        this.account = accounts[0];

        this.accessTokenRequest = {
            scopes: [
                ...protectedResources.apiXero.scopes.read
            ],
            authority: `https://login.microsoftonline.com/${msalConfig.auth.tenantId}/`,
            forceRefresh: forceRefresh,
            account: this.account,
        };

        this.accessTokenService = new AccessTokenHelper();

        this.xeroBaseUrl = useLocal
            ? "https://property.manager.internal.gateway/api/xero/dashboard"
            : "https://apim.ianrobertson.co.uk/api/xero/v1/dashboard";

        this.tenantsBaseUrl = useLocal ?
            "https://property.manager.internal.gateway/tenants/api/tenants/dashboard" :
            "https://apim.ianrobertson.co.uk/api/tenants/v1/dashboard";
    }


    getPeriodicBankTransactions = () => {
        // Create a variable to store auth errors that shouldn't be retried
        let authError = null;

        return polly()
            .logger(error => {
                console.error("An error occurred in getPeriodicBankTransactions", error);
            })
            .handle(err => {
                // If we've already detected an auth error, don't retry
                if (authError) return false;

                // Don't retry for 401/403 errors
                if (err.status === 401 || err.status === 403) {
                    authError = err; // Store the auth error
                    console.log(`Authorization error (${err.status}), not retrying`);
                    return false; // Don't retry
                }

                return true; // Retry for other errors
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise((resolve, reject) => {
                    // If we already have an auth error from a previous attempt, reject immediately
                    if (authError) {
                        return reject(authError);
                    }

                    if (this.inProgress !== InteractionStatus.None) {
                        return this.instance.acquireTokenSilent(this.accessTokenRequest);
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(accessTokenResponse => {
                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Content-Type", "application/json");

                            const options = {
                                method: "GET",
                                headers: headers
                            };

                            fetch(`${this.xeroBaseUrl}/getPeriodicBankTransactions`, options)
                                .then(response => {
                                    // First check if the response is ok (status in the range 200-299)
                                    if (!response.ok) {
                                        // Create an error object with status information
                                        const error = new Error(`HTTP Error: ${response.status} ${response.statusText}`);
                                        error.status = response.status;
                                        error.statusText = response.statusText;
                                        error.response = response;
                                        throw error; // This will be caught by the .catch() below
                                    }
                                    return response.json(); // Only try to parse JSON for successful responses
                                })
                                .then(data => {
                                    const response = cleanUpJsonForTransport(Object.assign({}, hydrateObjects(data)));
                                    if (!!response && response.received && response.spent) {
                                        const result = {
                                            received: Object.entries(response.received).map(r => r[1]),
                                            spent: Object.entries(response.spent).map(s => s[1]),
                                        };
                                        return resolve(result);
                                    }
                                    return resolve();
                                })
                                .catch(error => {
                                    console.log(error);
                                    // If this is an HTTP error we created above, we can access the status
                                    if (error.status) {
                                        console.log(`API returned status code: ${error.status}`);
                                        // Store auth errors so we don't retry
                                        if (error.status === 401 || error.status === 403) {
                                            authError = error;
                                            console.log("Authorization error, not retrying");
                                        }
                                    }
                                    return reject(error);
                                });
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError) {
                                //this.instance.acquireTokenRedirect(this.accessTokenRequest);
                            }
                            console.log(error);
                            return reject(error);
                        });
                });
            });
    };

    getPeriodicPaymentAllocations = () => {
        // Create a variable to store auth errors that shouldn't be retried
        let authError = null;

        return polly()
            .logger(error => {
                console.error("An error occurred in getPeriodicPaymentAllocations", error);
            })
            .handle(err => {
                // If we've already detected an auth error, don't retry
                if (authError) return false;

                // Don't retry for 401/403 errors
                if (err.status === 401 || err.status === 403) {
                    authError = err; // Store the auth error
                    console.log(`Authorization error (${err.status}), not retrying`);
                    return false; // Don't retry
                }

                return true; // Retry for other errors
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise((resolve, reject) => {
                    // If we already have an auth error from a previous attempt, reject immediately
                    if (authError) {
                        return reject(authError);
                    }

                    if (this.inProgress !== InteractionStatus.None) {
                        return this.instance.acquireTokenSilent(this.accessTokenRequest);
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(async accessTokenResponse => {
                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Content-Type", "application/json");

                            const options = {
                                method: "GET",
                                headers: headers
                            };

                            try {
                                const response = await fetch(`${this.tenantsBaseUrl}/getPeriodicPaymentAllocations`, options);
                                
                                // First check if the response is ok (status in the range 200-299)
                                if (!response.ok) {
                                    // Create an error object with status information
                                    const error = new Error(`HTTP Error: ${response.status} ${response.statusText}`);
                                    error.status = response.status;
                                    error.statusText = response.statusText;
                                    error.response = response;
                                    return reject(error);
                                }
                                
                                const data = await response.json(); // Only try to parse JSON for successful responses
                                const responseData = cleanUpJsonForTransport(Object.assign({}, hydrateObjects(data)));
                                
                                if (!!responseData && responseData.allocations) {
                                    const result = {
                                        allocations: Object.entries(responseData.allocations).map(a => a[1])
                                    };
                                    return resolve(result);
                                }
                                return resolve();
                            } catch (error) {
                                console.log(error);
                                // If this is an HTTP error we created above, we can access the status
                                if (error.status) {
                                    console.log(`API returned status code: ${error.status}`);
                                    // Store auth errors so we don't retry
                                    if (error.status === 401 || error.status === 403) {
                                        authError = error;
                                        console.log("Authorization error, not retrying");
                                    }
                                }
                                return reject(error);
                            }
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError) {
                                //this.instance.acquireTokenRedirect(this.accessTokenRequest);
                            }
                            console.log(error);
                            return reject(error);
                        });
                });
            });
    };

}